import React, { useState, useEffect } from 'react';
import clsx from 'clsx';
import { useSelector, useDispatch } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import { useHistory, Link } from 'react-router-dom';
import AppBar from '@material-ui/core/AppBar';
import Toolbar from '@material-ui/core/Toolbar';
import IconButton from '@material-ui/core/IconButton';
import Badge from '@material-ui/core/Badge';
import NotificationsIcon from '@material-ui/icons/Notifications';
import HelpIcon from '@material-ui/icons/Help';
import AccountIcon from '@material-ui/icons/AccountCircle';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import NotificationList from '../common/NotificationList';
import { selectOpen, selectNumberOfNotifications } from '../../store/layoutSlice';
import {
  signOut,
  selectUsername,
  selectUserClientId,
  selectIsAuthenticated,
} from '../../store/authSlice';
import {
  resetWidget,
  getCompanyByClientId,
  setCompanyCode,
  selectCompany,
} from '../../store/widgetSlice';

const drawerWidth = 180;
const mobileDrawerWidth = 60;

const useStyles = makeStyles((theme) => ({
  appBar: {
    zIndex: theme.zIndex.drawer + 1,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    backgroundColor: 'white',
  },
  appBarShift: {
    marginLeft: mobileDrawerWidth,
    width: `calc(100% - ${mobileDrawerWidth}px)`,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),

    [theme.breakpoints.up('md')]: {
      marginLeft: drawerWidth,
      width: `calc(100% - ${drawerWidth}px)`,
    },
  },
  menuButton: {
    marginRight: 36,
  },
  menuButtonHidden: {
    display: 'none',
  },
  grow: {
    flexGrow: 1,
  },
  link: {
    display: 'flex',
    marginTop: theme.spacing(1),
  },
  logo: {
    height: 56,
    width: 'auto',
  },
  toolbar: {
    padding: theme.spacing(0, 2, 0, 1),
  },
}));

export default function AppHeader() {
  const classes = useStyles();
  const history = useHistory();
  const open = useSelector(selectOpen);
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const numberOfNotifications = useSelector(selectNumberOfNotifications);
  const username = useSelector(selectUsername);
  const clientId = useSelector(selectUserClientId);
  const company = useSelector(selectCompany);

  const dispatch = useDispatch();
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [hasNewNotifications, setHasNewNotifications] = useState(true);

  const handleClick = (event) => {
    setMenuAnchor(event.currentTarget);
  };

  const handleClose = () => {
    setMenuAnchor(null);
  };

  const attemptSignOut = () => {
    dispatch(signOut());
    dispatch(resetWidget());
    handleClose();
  };

  useEffect(() => {
    if (!isAuthenticated) {
      history.push('/login');
    }
  }, [isAuthenticated, history]);

  useEffect(() => {
    if (clientId) {
      dispatch(getCompanyByClientId({ clientId }));
    }
  }, [dispatch, clientId]);

  useEffect(() => {
    dispatch(setCompanyCode(company?.companyCode || username));
  }, [dispatch, company, username]);

  return (
    <AppBar
      position="absolute"
      color="default"
      elevation={1}
      className={clsx(classes.appBar, open && classes.appBarShift)}
    >
      <Toolbar className={classes.toolbar}>
        <div className={classes.grow} />
        <IconButton id="notifications-button" color="inherit" onClick={handleClick}>
          <Badge
            invisible={!numberOfNotifications || !hasNewNotifications}
            badgeContent={numberOfNotifications}
            color="secondary"
          >
            <NotificationsIcon />
          </Badge>
        </IconButton>
        <IconButton
          color="inherit"
          component={Link}
          target="_blank"
          rel="noopener noreferrer"
          to={{ pathname: 'https://guestranger.zohodesk.com/portal/en/kb' }}
        >
          <HelpIcon />
        </IconButton>
        <IconButton id="account-button" color="inherit" onClick={handleClick}>
          <AccountIcon />
        </IconButton>
      </Toolbar>
      <Menu
        id="account-menu"
        anchorEl={menuAnchor}
        open={!!menuAnchor && menuAnchor.id === 'account-button'}
        onClose={handleClose}
        keepMounted
      >
        <MenuItem onClick={attemptSignOut}>Sign Out</MenuItem>
      </Menu>
      <Menu
        id="notifications-menu"
        anchorEl={menuAnchor}
        open={!!menuAnchor && menuAnchor.id === 'notifications-button'}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        onEnter={() => setHasNewNotifications(false)}
        onClose={handleClose}
        keepMounted
      >
        <NotificationList />
      </Menu>
    </AppBar>
  );
}
